import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { languageService } from '../services/languageService';
import { SUPPORTED_LANGUAGES } from '../models/Language';

// Create the Language Context
const LanguageContext = createContext();

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [availableLanguages, setAvailableLanguages] = useState(SUPPORTED_LANGUAGES);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [multilingualTexts, setMultilingualTexts] = useState({});

  // Local storage key for language preference
  const LANGUAGE_STORAGE_KEY = 'selected_language_code';

  /**
   * Save language code to localStorage
   */
  const saveLanguageCode = useCallback((code) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
      console.log('[LanguageProvider] Saved language code to storage:', code);
    } catch (err) {
      console.error('[LanguageProvider] Error saving language code:', err);
    }
  }, []);

  /**
   * Fetch available languages from API
   */
  const fetchAvailableLanguages = useCallback(async () => {
    // Intentionally skip remote fetch to avoid API on home load
    console.log('[LanguageProvider] Using local SUPPORTED_LANGUAGES (no API call)');
    setAvailableLanguages(SUPPORTED_LANGUAGES);
    setError(null);
    return SUPPORTED_LANGUAGES;
  }, []);

  /**
   * Initialize language settings
   */
  const initialize = useCallback(async () => {
    console.log('[LanguageProvider] Initializing language settings (no remote fetch)');
    setError(null);
    // Immediately set available languages to local list without API
    setAvailableLanguages(SUPPORTED_LANGUAGES);
    setIsLoading(false);
    console.log('[LanguageProvider] Initialization complete with local languages');
  }, []);

  /**
   * Change the selected language
   */
  const changeLanguage = useCallback(async (language) => {
    if (selectedLanguage?.code === language.code) {
      console.log('[LanguageProvider] Language already selected:', language.code);
      return;
    }

    console.log('[LanguageProvider] Changing language to:', language.name);
    const previousLanguage = selectedLanguage;
    
    setSelectedLanguage(language);
    saveLanguageCode(language.code);
    
    // Clear existing multilingual texts when language changes
    setMultilingualTexts({});
    
    console.log(`[LanguageProvider] Language changed from ${previousLanguage?.name || 'none'} to ${language.name}`);
  }, [selectedLanguage, saveLanguageCode]);

  /**
   * Get multilingual text for given keys
   */
  const getMultilingualText = useCallback(async (keys) => {
    if (!selectedLanguage || !keys || keys.length === 0) {
      return {};
    }

    const languageCode = selectedLanguage.code;
    const cacheKey = `${languageCode}_${keys.join('_')}`;
    
    // Return cached text if available
    if (multilingualTexts[cacheKey]) {
      return multilingualTexts[cacheKey];
    }

    // Provide fallback translations to prevent API calls and errors
    console.log('[LanguageProvider] Using fallback translations for keys:', keys);
    
    const fallbackTexts = {};
    keys.forEach(key => {
      // Convert snake_case keys to Title Case as fallback
      const fallbackText = key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Specific mappings for common menu items
      const keyMappings = {
        'my_applications': 'My Applications',
        'my_referrals': 'My Referrals',
        'my_earnings': 'My Earnings',
        'sign_in': 'Sign In',
        'profile': 'Profile',
        'home': 'Home',
        'jobs': 'Jobs',
        'notifications': 'Notifications'
      };
      
      fallbackTexts[key] = keyMappings[key] || fallbackText;
    });
    
    // Cache the fallback result
    setMultilingualTexts(prev => ({
      ...prev,
      [cacheKey]: fallbackTexts
    }));
    
    return Promise.resolve(fallbackTexts);
  }, [selectedLanguage, multilingualTexts]);

  /**
   * Convert text to target language
   */
  const convertText = useCallback(async (inputText, targetLanguageCode) => {
    if (!inputText) return inputText;
    
    try {
      const translatedText = await languageService.convertInputToMultilingual(
        inputText, 
        targetLanguageCode || selectedLanguage?.code || 'en'
      );
      return translatedText;
    } catch (err) {
      console.error('[LanguageProvider] Error converting text:', err);
      return inputText;
    }
  }, [selectedLanguage]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Update selected language when available languages change
  useEffect(() => {
    if (availableLanguages.length > 0 && !selectedLanguage) {
      const storedLanguageCode = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      const defaultLanguage = availableLanguages.find(lang => 
        lang.code === (storedLanguageCode || 'en')
      ) || availableLanguages[0];
      
      setSelectedLanguage(defaultLanguage);
      if (storedLanguageCode !== defaultLanguage.code) {
        saveLanguageCode(defaultLanguage.code);
      }
      console.log('[LanguageProvider] Set language from available languages:', defaultLanguage.name);
    }
  }, [availableLanguages, selectedLanguage, saveLanguageCode]);

  // Sync language across tabs via storage events
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LANGUAGE_STORAGE_KEY) return;
      const newCode = e.newValue;
      if (!newCode || newCode === selectedLanguage?.code) return;
      const nextLang = availableLanguages.find(l => l.code === newCode) ||
        availableLanguages.find(l => l.code === 'en') ||
        availableLanguages[0];
      if (nextLang) {
        console.log('[LanguageProvider] Storage event detected, switching language to:', nextLang.name);
        setSelectedLanguage(nextLang);
        // Clear cached multilingual texts when language changes externally
        setMultilingualTexts({});
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [availableLanguages, selectedLanguage]);

  const value = {
    // State
    availableLanguages,
    selectedLanguage,
    isLoading,
    error,
    
    // Actions
    changeLanguage,
    fetchAvailableLanguages,
    getMultilingualText,
    convertText,
    initialize,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use Language Context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
